import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../../components/ui/table";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "../shared";
import { ACCOUNTING_BASE } from "../accountingNav";
import { ReportFilterBar, ReportPrintShell, useFinancialYears, downloadReportCsv, formatCurrency } from "./reportShared";

/**
 * Phase 8 - Income & Expenditure (items 20-23). The housing-society operating
 * statement: every Income/Expense ledger's own PERIOD movement, Surplus or
 * Deficit named explicitly (never a generic "Net" - item 21), an optional
 * Previous Period comparison (item 22, never a blocker if it doesn't exist),
 * and drill-down into General Ledger per head (item 23).
 */
export default function IncomeExpenditurePage() {
  const navigate = useNavigate();
  const financialYears = useFinancialYears();
  const [financialYearId, setFinancialYearId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [comparePrevious, setComparePrevious] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = { financialYearId, fromDate, toDate, comparePrevious };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reports/income-expenditure", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load income & expenditure"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [financialYearId, fromDate, toDate, comparePrevious]);

  const openLedger = (ledgerId) => navigate(`${ACCOUNTING_BASE}/reports/general-ledger?ledgerId=${ledgerId}&financialYearId=${financialYearId || ""}&fromDate=${fromDate || data?.fromDate || ""}&toDate=${toDate || data?.toDate || ""}`);

  const prevByName = (section) => {
    const map = {};
    (data?.previousPeriod?.[section] || []).forEach((r) => { map[r.ledgerName] = r.amount; });
    return map;
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Income &amp; Expenditure</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Operating statement for this period, with Surplus / Deficit at the bottom.</p>
      </div>

      <div className="report-screen-only space-y-5">
        <ReportFilterBar
          financialYears={financialYears} financialYearId={financialYearId} onFinancialYearChange={setFinancialYearId}
          fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate}
          extra={
            <label className="flex items-center gap-2 pb-2 text-sm text-[#374151]">
              <input type="checkbox" className="h-4 w-4" checked={comparePrevious} onChange={(e) => setComparePrevious(e.target.checked)} /> Compare previous period
            </label>
          }
          onExport={() => downloadReportCsv("/society/accounting/reports/income-expenditure", params, `income-expenditure-${data?.fromDate || ""}-to-${data?.toDate || ""}.csv`)}
          onPrint={() => window.print()}
        />

        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : !data || (data.income.length === 0 && data.expenditure.length === 0) ? (
          <EmptyState>No Income or Expense activity in this range yet.</EmptyState>
        ) : (
          <>
            <SectionCard title="Income" subtitle={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}>
              <IETable rows={data.income} total={data.totalIncome} prev={comparePrevious ? prevByName("income") : null} prevTotal={data.previousPeriod?.totalIncome} onOpen={openLedger} />
            </SectionCard>
            <SectionCard title="Expenditure">
              <IETable rows={data.expenditure} total={data.totalExpenditure} prev={comparePrevious ? prevByName("expenditure") : null} prevTotal={data.previousPeriod?.totalExpenditure} onOpen={openLedger} />
            </SectionCard>
            <div className={`rounded-sm border p-4 text-center text-lg font-extrabold ${data.isDeficit ? "border-red-300 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
              {data.isDeficit ? `Deficit: ${formatCurrency(data.deficit)}` : `Surplus: ${formatCurrency(data.surplus)}`}
            </div>
          </>
        )}
      </div>

      {data && (
        <div className="report-print-only">
          <ReportPrintShell reportName="Income & Expenditure" rangeLabel={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}>
            <div className="mb-1 font-bold">Income</div>
            <PrintIETable rows={data.income} total={data.totalIncome} />
            <div className="mb-1 mt-3 font-bold">Expenditure</div>
            <PrintIETable rows={data.expenditure} total={data.totalExpenditure} />
            <div className="mt-3 font-bold">{data.isDeficit ? `Deficit: ${formatCurrency(data.deficit)}` : `Surplus: ${formatCurrency(data.surplus)}`}</div>
          </ReportPrintShell>
        </div>
      )}
    </div>
  );
}

function IETable({ rows, total, prev, prevTotal, onOpen }) {
  return (
    <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Head</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            {prev && <TableHead className="text-right">Previous Period</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.ledgerId}>
              <TableCell><button type="button" className="text-left font-semibold text-[#111827] hover:underline" onClick={() => onOpen(r.ledgerId)}>{r.ledgerName}</button></TableCell>
              <TableCell className="text-right">{formatCurrency(r.amount)}</TableCell>
              {prev && <TableCell className="text-right text-[#6B7280]">{prev[r.ledgerName] !== undefined ? formatCurrency(prev[r.ledgerName]) : "—"}</TableCell>}
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={prev ? 3 : 2} className="text-center text-sm text-[#6B7280]">None</TableCell></TableRow>}
          <TableRow className="border-t-2 border-[#111827] font-bold">
            <TableCell>Total</TableCell>
            <TableCell className="text-right">{formatCurrency(total)}</TableCell>
            {prev && <TableCell className="text-right">{prevTotal !== undefined ? formatCurrency(prevTotal) : "—"}</TableCell>}
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function PrintIETable({ rows, total }) {
  return (
    <table className="w-full border-collapse text-[10px]">
      <tbody>
        {rows.map((r) => (
          <tr key={r.ledgerId} className="border-b border-gray-300"><td className="py-0.5">{r.ledgerName}</td><td className="py-0.5 text-right">{formatCurrency(r.amount)}</td></tr>
        ))}
        <tr className="border-t border-black font-bold"><td className="py-1">Total</td><td className="py-1 text-right">{formatCurrency(total)}</td></tr>
      </tbody>
    </table>
  );
}
